<!-- Preview Mode -->
<div *ngIf="mode === 'preview'" [ngStyle]="getPreviewStyles()" class="prose max-w-none p-6 rounded-lg">
  <div *ngIf="getHeading()" class="mb-6">
    <h2 [style.color]="getHeadingColor()" [ngClass]="getTitlePositionClass()" class="text-3xl font-bold">{{ getHeading() }}</h2>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="flex justify-center items-center py-12">
    <div class="text-gray-400">Nieuws laden...</div>
  </div>

  <!-- No Posts State -->
  <div *ngIf="!loading && !hasPosts()" class="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
    <h3 class="mt-2 text-sm font-medium text-gray-900">Geen nieuwsberichten</h3>
    <p class="mt-1 text-sm text-gray-500">Er zijn nog geen gepubliceerde berichten om te tonen.</p>
  </div>

  <!-- Cards Layout -->
  <div *ngIf="!loading && hasPosts() && getLayoutType() === 'cards'"
       class="grid gap-6" [ngClass]="getGridColumnsClass()">
    <article *ngFor="let post of posts"
             class="rounded-lg overflow-hidden transition-all duration-300"
             [ngClass]="getCardClasses()">

      <!-- Image -->
      <div *ngIf="getShowImage() && getPostImage(post) && getImagePosition() === 'top'"
           class="overflow-hidden"
           [style.height.px]="getImageHeight()">
        <img [src]="getPostImage(post)"
             [alt]="getPostTitle(post)"
             class="w-full h-full object-cover">
      </div>

      <div class="p-4">
        <!-- Date -->
        <div *ngIf="getShowDate()" class="text-xs text-gray-500 mb-2">
          {{ getPostDate(post) }}
        </div>

        <!-- Title -->
        <h3 [style.color]="getTextColor()" class="text-lg font-semibold mb-2 line-clamp-2">
          {{ getPostTitle(post) }}
        </h3>

        <!-- Excerpt -->
        <p *ngIf="getShowExcerpt()" [style.color]="getTextColor()" class="text-sm opacity-80 mb-4 line-clamp-3">
          {{ getPostExcerpt(post) }}
        </p>

        <!-- Read More -->
        <a *ngIf="getShowReadMore()"
           [href]="getPostLink(post)"
           class="text-indigo-600 hover:text-indigo-800 text-sm font-medium inline-flex items-center">
          {{ getReadMoreText() }}
          <svg class="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </a>
      </div>
    </article>
  </div>

  <!-- List Layout -->
  <div *ngIf="!loading && hasPosts() && getLayoutType() === 'list'" class="space-y-4">
    <article *ngFor="let post of posts"
             class="flex rounded-lg overflow-hidden transition-all duration-300"
             [ngClass]="getCardClasses()">

      <!-- Image Left -->
      <div *ngIf="getShowImage() && getPostImage(post)"
           class="flex-shrink-0 w-32 md:w-48">
        <img [src]="getPostImage(post)"
             [alt]="getPostTitle(post)"
             class="w-full h-full object-cover">
      </div>

      <div class="flex-1 p-4">
        <!-- Date -->
        <div *ngIf="getShowDate()" class="text-xs text-gray-500 mb-2">
          {{ getPostDate(post) }}
        </div>

        <!-- Title -->
        <h3 [style.color]="getTextColor()" class="text-lg font-semibold mb-2">
          {{ getPostTitle(post) }}
        </h3>

        <!-- Excerpt -->
        <p *ngIf="getShowExcerpt()" [style.color]="getTextColor()" class="text-sm opacity-80 mb-4">
          {{ getPostExcerpt(post) }}
        </p>

        <!-- Read More -->
        <a *ngIf="getShowReadMore()"
           [href]="getPostLink(post)"
           class="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
          {{ getReadMoreText() }} →
        </a>
      </div>
    </article>
  </div>

  <!-- Grid Layout (simpler than cards) -->
  <div *ngIf="!loading && hasPosts() && getLayoutType() === 'grid'"
       class="grid gap-6" [ngClass]="getGridColumnsClass()">
    <article *ngFor="let post of posts" class="group">

      <!-- Image -->
      <div *ngIf="getShowImage() && getPostImage(post)"
           class="rounded-lg overflow-hidden mb-3"
           [style.height.px]="getImageHeight()">
        <img [src]="getPostImage(post)"
             [alt]="getPostTitle(post)"
             class="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105">
      </div>

      <!-- Date -->
      <div *ngIf="getShowDate()" class="text-xs text-gray-500 mb-1">
        {{ getPostDate(post) }}
      </div>

      <!-- Title -->
      <h3 [style.color]="getTextColor()" class="text-lg font-semibold mb-2 group-hover:text-indigo-600 transition-colors">
        <a [href]="getPostLink(post)">{{ getPostTitle(post) }}</a>
      </h3>

      <!-- Excerpt -->
      <p *ngIf="getShowExcerpt()" [style.color]="getTextColor()" class="text-sm opacity-80">
        {{ getPostExcerpt(post) }}
      </p>
    </article>
  </div>

  <!-- Compact Layout -->
  <div *ngIf="!loading && hasPosts() && getLayoutType() === 'compact'" class="divide-y divide-gray-200">
    <article *ngFor="let post of posts" class="py-4 first:pt-0 last:pb-0">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <h3 [style.color]="getTextColor()" class="font-semibold hover:text-indigo-600 transition-colors">
            <a [href]="getPostLink(post)">{{ getPostTitle(post) }}</a>
          </h3>
        </div>
        <div *ngIf="getShowDate()" class="text-xs text-gray-500 ml-4">
          {{ getPostDate(post) }}
        </div>
      </div>
    </article>
  </div>

  <!-- Featured Layout -->
  <div *ngIf="!loading && hasPosts() && getLayoutType() === 'featured'" class="space-y-6">
    <!-- Featured Post -->
    <article *ngIf="getFeaturedPost()" class="rounded-lg overflow-hidden" [ngClass]="getCardClasses()">
      <div class="md:flex">
        <div *ngIf="getShowImage() && getPostImage(getFeaturedPost()!)"
             class="md:w-1/2">
          <img [src]="getPostImage(getFeaturedPost()!)"
               [alt]="getPostTitle(getFeaturedPost()!)"
               class="w-full h-64 md:h-full object-cover">
        </div>
        <div class="p-6 md:w-1/2 flex flex-col justify-center">
          <div *ngIf="getShowDate()" class="text-xs text-gray-500 mb-2">
            {{ getPostDate(getFeaturedPost()!) }}
          </div>
          <h3 [style.color]="getTextColor()" class="text-2xl font-bold mb-3">
            {{ getPostTitle(getFeaturedPost()!) }}
          </h3>
          <p *ngIf="getShowExcerpt()" [style.color]="getTextColor()" class="text-sm opacity-80 mb-4">
            {{ getPostExcerpt(getFeaturedPost()!) }}
          </p>
          <a *ngIf="getShowReadMore()"
             [href]="getPostLink(getFeaturedPost()!)"
             class="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center">
            {{ getReadMoreText() }}
            <svg class="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </article>

    <!-- Remaining Posts Grid -->
    <div *ngIf="getRemainingPosts().length > 0" class="grid gap-6" [ngClass]="getGridColumnsClass()">
      <article *ngFor="let post of getRemainingPosts()"
               class="rounded-lg overflow-hidden transition-all duration-300"
               [ngClass]="getCardClasses()">

        <div *ngIf="getShowImage() && getPostImage(post)"
             class="overflow-hidden h-32">
          <img [src]="getPostImage(post)"
               [alt]="getPostTitle(post)"
               class="w-full h-full object-cover">
        </div>

        <div class="p-3">
          <div *ngIf="getShowDate()" class="text-xs text-gray-500 mb-1">
            {{ getPostDate(post) }}
          </div>
          <h3 [style.color]="getTextColor()" class="text-sm font-semibold line-clamp-2">
            <a [href]="getPostLink(post)" class="hover:text-indigo-600 transition-colors">
              {{ getPostTitle(post) }}
            </a>
          </h3>
        </div>
      </article>
    </div>
  </div>
</div>

<!-- Edit Mode -->
<div *ngIf="mode === 'edit'">
  <p class="text-sm text-gray-400">Gebruik de sidebar om nieuwsberichten en weergave opties aan te passen.</p>
</div>
